import { formatDate } from '@enter/core';
import Icon from '@mdi/react';
import { mdiAutoFix, mdiCheck, mdiClipboardListOutline } from '@mdi/js';
import React from 'react';
import { Variables } from '../../base';
import * as S from './statement-ledger-dialog.styles';
import {
    ApplyMode,
    buildFifoNote,
    formatLedgerMoney,
    StatementClaimOption,
} from './statement-apply-to-claims.utils';

type StatementApplyToClaimsProps = {
    applyMode: ApplyMode;
    onApplyModeChange: (mode: ApplyMode) => void;
    openClaims: StatementClaimOption[];
    selectedClaimIds: Record<string, boolean>;
    onToggleClaim: (claimId: string) => void;
    targetClaims: StatementClaimOption[];
    errorMessage?: string;
};

export const StatementApplyToClaims: React.FunctionComponent<StatementApplyToClaimsProps> = ({
    applyMode,
    onApplyModeChange,
    openClaims,
    selectedClaimIds,
    onToggleClaim,
    targetClaims,
    errorMessage,
}) => {
    const selectedCount = targetClaims.length;
    const selectedBalanceTotal = targetClaims.reduce((sum, claim) => sum + claim.balance, 0);
    const fifoNote = buildFifoNote(openClaims);

    return (
        <S.FormSection>
            <S.Label>Apply to</S.Label>
            <S.ApplyModeRow>
                <S.ApplyModeButton
                    type="button"
                    variant="unstyled"
                    $active={applyMode === 'auto'}
                    onClick={() => onApplyModeChange('auto')}>
                    <Icon path={mdiAutoFix} size={0.7} />
                    Recommended
                </S.ApplyModeButton>
                <S.ApplyModeButton
                    type="button"
                    variant="unstyled"
                    $active={applyMode === 'manual'}
                    onClick={() => onApplyModeChange('manual')}>
                    <Icon path={mdiClipboardListOutline} size={0.7} />
                    Select claims
                </S.ApplyModeButton>
            </S.ApplyModeRow>

            {applyMode === 'auto' ? (
                <S.FifoNote>
                    <Icon path={mdiAutoFix} size={0.7} color={Variables.Colors.b300} />
                    <span>{fifoNote}</span>
                </S.FifoNote>
            ) : (
                <>
                    <S.ClaimList>
                        {openClaims.map(claim => {
                            const checked = !!selectedClaimIds[claim.id];
                            return (
                                <S.ClaimRow
                                    key={claim.id}
                                    type="button"
                                    variant="unstyled"
                                    onClick={() => onToggleClaim(claim.id)}>
                                    <S.CheckBox checked={checked}>
                                        {checked && (
                                            <Icon
                                                path={mdiCheck}
                                                size={0.55}
                                                color={Variables.Swatches.White}
                                            />
                                        )}
                                    </S.CheckBox>
                                    <S.ClaimMeta>
                                        <S.ClaimTitle>Claim #{claim.externalId || '—'}</S.ClaimTitle>
                                        <S.ClaimSubtitle>
                                            {claim.fromDos
                                                ? formatDate(claim.fromDos, 'MMM dd, yyyy')
                                                : '—'}
                                            {' · '}
                                            {claim.pendingAdjudication ? 'Pending' : 'Open balance'}
                                        </S.ClaimSubtitle>
                                    </S.ClaimMeta>
                                    <S.ClaimBalance>{formatLedgerMoney(claim.balance)}</S.ClaimBalance>
                                </S.ClaimRow>
                            );
                        })}
                        {!openClaims.length && (
                            <S.ClaimRow type="button" variant="unstyled" disabled>
                                <S.ClaimMeta>
                                    <S.ClaimSubtitle>No claims with an open balance</S.ClaimSubtitle>
                                </S.ClaimMeta>
                            </S.ClaimRow>
                        )}
                    </S.ClaimList>
                    <S.ApplySummary>
                        <span>
                            {selectedCount} of {openClaims.length} claims selected
                        </span>
                        <S.ApplySummaryTotal>
                            {formatLedgerMoney(selectedBalanceTotal)} selected
                        </S.ApplySummaryTotal>
                    </S.ApplySummary>
                </>
            )}
            {errorMessage && <S.ErrorText>{errorMessage}</S.ErrorText>}
        </S.FormSection>
    );
};
